import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@screentone/addon-auth-wrapper';
import type { ComboboxItem } from '@screentone/core';
import {
  Button,
  Checkbox,
  Dialog,
  FormHelperText,
  FormLabel,
  Group,
  IconPlusCircle,
  IconTrash,
  Input,
  MultiSelect,
  Select,
  Switch,
  Table,
  Typography
} from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';

import { ConfirmationDialog, ResponsiveLoader } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import {
  AvailableFeatureFlags,
  FeatureFlag,
  FeatureFlagInput,
  PublicationSettingUpdateInput,
  useFeatureFlagUpsertMutation,
  usePublicationSettingUpdateMutation
} from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import { ROLE_TYPE } from 'hooks/roles/types';
import useRequireRoles from 'hooks/roles/useRequireRoles';
import { usePrompt } from 'hooks/useBeforeLeavePage';
import SiteConfigPage from 'pages/site-config/shared/SiteConfigPage';
import styles from './styles.module.scss';

const AVAILABLE_FEATURE_FLAGS = Object.values(AvailableFeatureFlags);
const GLOBAL_PUBLICATION_KEY = 'global';

type Draft = {
  name: AvailableFeatureFlags | '';
  description: string;
  enabled: boolean;
  targetPublications: string[];
};

const EMPTY_DRAFT: Draft = {
  name: '',
  description: '',
  enabled: true,
  targetPublications: []
};

function SiteConfigFeatureFlags() {
  useRequireRoles(ROLE_TYPE.ADMIN);
  const currentProperty = useConvertedProperty();
  const publicationSettingsQueryVariables = { publicationKey: currentProperty ?? '' };
  const { data, error } = usePublicationSettings(publicationSettingsQueryVariables);
  const queryClient = useQueryClient();
  const { alertSuccess, alertError } = useAlert();
  const { user } = useAuth();
  const { mutate, isLoading: isUpdating } = usePublicationSettingUpdateMutation();
  const { mutate: upsertFeatureFlag, isLoading: isUpserting } = useFeatureFlagUpsertMutation();

  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [hasChanged, setHasChanged] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FeatureFlag | null>(null);

  useEffect(() => {
    if (data) {
      setFlags(data.publicationSetting.featureFlags ?? []);
      setHasChanged(false);
    }
  }, [data]);

  usePrompt('You have unsaved changes. Are you sure you want to leave?', hasChanged);

  const suggestedNames = useMemo(() => {
    const used = new Set<string>(flags.map((f) => f.name));
    return AVAILABLE_FEATURE_FLAGS.filter((n) => !used.has(n));
  }, [flags]);

  const publicationItems = useMemo<ComboboxItem[]>(() => {
    const props = user?.app_properties ?? [];
    return props
      .filter((p) => p.value !== GLOBAL_PUBLICATION_KEY)
      .map((p) => ({ id: p.value, label: p.name, value: p.value }));
  }, [user]);

  const selectedPublicationItems = useMemo<ComboboxItem[]>(
    () => publicationItems.filter((p) => draft.targetPublications.includes(p.value as string)),
    [publicationItems, draft.targetPublications]
  );

  const applyToAll =
    publicationItems.length > 0 && draft.targetPublications.length === publicationItems.length;

  const toggleApplyToAll = () => {
    setDraft((prev) => ({
      ...prev,
      targetPublications: applyToAll ? [] : publicationItems.map((p) => p.value as string)
    }));
  };

  const openAddModal = () => {
    setDraft({
      ...EMPTY_DRAFT,
      targetPublications: currentProperty && currentProperty !== GLOBAL_PUBLICATION_KEY ? [currentProperty] : []
    });
    setDraftError(null);
    setAddOpen(true);
  };

  const closeAddModal = () => {
    setAddOpen(false);
    setDraft(EMPTY_DRAFT);
    setDraftError(null);
  };

  const handleAdd = () => {
    if (!draft.name) {
      setDraftError('Name is required.');
      return;
    }
    if (!draft.description.trim()) {
      setDraftError('Description is required.');
      return;
    }
    if (draft.targetPublications.length === 0) {
      setDraftError('Select at least one publication.');
      return;
    }

    const flagName = draft.name;
    const flagDescription = draft.description.trim();
    const flagEnabled = draft.enabled;

    upsertFeatureFlag(
      {
        input: {
          publicationKeys: draft.targetPublications,
          featureFlag: { name: flagName, description: flagDescription, enabled: flagEnabled }
        }
      },
      {
        onSuccess: async (result) => {
          const updatedKeys = result.featureFlagUpsert.map((p) => p.publicationKey);
          alertSuccess(`Feature flag "${flagName}" written to: ${updatedKeys.join(', ')}`);

          if (currentProperty && updatedKeys.includes(currentProperty)) {
            const currentUpdated = result.featureFlagUpsert.find((p) => p.publicationKey === currentProperty);
            setFlags(currentUpdated?.featureFlags ?? []);
            setHasChanged(false);
          }

          await queryClient.invalidateQueries({ queryKey: ['PublicationSettings'], exact: false });
          closeAddModal();
        },
        onError: (err) => {
          setDraftError((err as Error).message || 'Failed to write feature flag.');
        }
      }
    );
  };

  const updateFlag = (name: AvailableFeatureFlags, patch: Partial<FeatureFlag>) => {
    setFlags((prev) => prev.map((f) => (f.name === name ? { ...f, ...patch } : f)));
    setHasChanged(true);
  };

  const handleDeleteConfirm = () => {
    if (!pendingDelete) return;
    setFlags((prev) => prev.filter((f) => f.name !== pendingDelete.name));
    setHasChanged(true);
    setPendingDelete(null);
  };

  const hasInvalidFlag = flags.some((f) => !f.description.trim());

  const handleSave = () => {
    if (!data?.publicationSetting || !currentProperty) return;

    if (hasInvalidFlag) {
      alertError('Cannot save — every feature flag needs a description.');
      return;
    }

    const cleanedFlags: FeatureFlagInput[] = flags.map((f) => ({
      name: f.name,
      description: f.description,
      enabled: f.enabled
    }));

    const input: PublicationSettingUpdateInput = {
      ...data.publicationSetting,
      featureFlags: cleanedFlags.length > 0 ? cleanedFlags : null
    };

    mutate(
      { publicationSettingUpdateInput: input },
      {
        onSuccess: async () => {
          setHasChanged(false);
          alertSuccess('Feature flags saved successfully');
          await queryClient.invalidateQueries({
            queryKey: ['PublicationSettings', publicationSettingsQueryVariables],
            exact: false
          });
        },
        onError: (err) => {
          alertError(`Failed to save feature flags. ${(err as Error).message}`);
        }
      }
    );
  };

  const titleActions = data ? (
    <>
      <Button tertiary icon={IconPlusCircle} onClick={openAddModal}>
        Add feature flag
      </Button>
      <Button primary onClick={handleSave} disabled={!hasChanged || isUpdating || hasInvalidFlag}>
        {isUpdating ? 'Saving…' : 'Save'}
      </Button>
    </>
  ) : null;

  if (error) {
    return (
      <SiteConfigPage title="Feature Flags">Failed to load feature flags.</SiteConfigPage>
    );
  }

  if (!data) {
    return (
      <SiteConfigPage title="Feature Flags">
        <ResponsiveLoader />
      </SiteConfigPage>
    );
  }

  return (
    <>
      <SiteConfigPage title="Feature Flags" actions={titleActions}>
        <Typography variant="note">
          Per-publication overrides on top of the account-level GrowthBook config. Wrong values here can silently
          disable core features for this brand.
        </Typography>
        <Table margin={{ top: 'lg' }} fullWidth>
          <Table.Head>
            <Table.Row>
              <Table.Cell scope="col">Name</Table.Cell>
              <Table.Cell scope="col" align="left">
                Description
              </Table.Cell>
              <Table.Cell scope="col" align="right" className={styles.shrinkCol}>
                Enabled
              </Table.Cell>
              <Table.Cell scope="col" align="right" className={styles.shrinkCol}>
                Actions
              </Table.Cell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {flags.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan="4">
                  <Typography variant="note">No feature flags configured for this publication.</Typography>
                </Table.Cell>
              </Table.Row>
            )}
            {flags.map((flag) => {
              const descriptionInvalid = !flag.description.trim();
              return (
                <Table.Row key={flag.name}>
                  <Table.Cell>{flag.name}</Table.Cell>
                  <Table.Cell align="left">
                    <Input
                      value={flag.description}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        updateFlag(flag.name, { description: e.target.value })
                      }
                      error={descriptionInvalid}
                      placeholder="Describe what this flag controls"
                    />
                    {descriptionInvalid && <FormHelperText error>Description is required.</FormHelperText>}
                  </Table.Cell>
                  <Table.Cell align="right" className={styles.shrinkCol}>
                    <Switch
                      checked={flag.enabled}
                      onChange={() => updateFlag(flag.name, { enabled: !flag.enabled })}
                    />
                  </Table.Cell>
                  <Table.Cell align="right" className={styles.shrinkCol}>
                    <Button
                      size="sm"
                      tertiary
                      icon={IconTrash}
                      color="lava"
                      onClick={() => setPendingDelete(flag)}
                    />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </SiteConfigPage>

      {addOpen && (
        <Dialog status="open" onDismiss={closeAddModal}>
          <Dialog.Title>Add feature flag</Dialog.Title>
          <Dialog.Content>
            <Group gap="md" direction="column" fullWidth>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Name" labelPosition="top" required fullWidth />
                <Select
                  value={draft.name}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setDraft((prev) => ({ ...prev, name: e.target.value as AvailableFeatureFlags | '' }))
                  }
                  error={!!draftError && !draft.name}
                  disabled={suggestedNames.length === 0}
                >
                  <option value="">Choose a feature flag…</option>
                  {suggestedNames.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
                <FormHelperText>
                  {suggestedNames.length === 0
                    ? 'All known feature flags have already been added for this brand.'
                    : "Only known feature flags this brand hasn't added yet are listed."}
                </FormHelperText>
              </Group>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Description" labelPosition="top" required fullWidth />
                <Input
                  value={draft.description}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setDraft((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="Describe what this flag controls"
                  error={!!draftError && !draft.description.trim()}
                />
              </Group>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Publications" labelPosition="top" required fullWidth />
                <FormLabel label="Apply to all publications" labelPosition="right">
                  <Checkbox checked={applyToAll} onChange={toggleApplyToAll} />
                </FormLabel>
                <MultiSelect
                  items={publicationItems}
                  selectedItems={selectedPublicationItems}
                  onSelectedItemsChange={(items) => {
                    const resolved = typeof items === 'function' ? items(selectedPublicationItems) : items;
                    setDraft((prev) => ({
                      ...prev,
                      targetPublications: resolved.map((it) => it.value as string)
                    }));
                  }}
                  status="ready"
                />
                <FormHelperText>
                  The flag is written to every selected publication in a single call. Tick &quot;Apply to all
                  publications&quot; to preselect every publication you have access to; you can then deselect any that
                  shouldn&apos;t be included.
                </FormHelperText>
              </Group>
              <FormLabel label="Enabled" labelPosition="right">
                <Switch
                  checked={draft.enabled}
                  onChange={() => setDraft((prev) => ({ ...prev, enabled: !prev.enabled }))}
                />
              </FormLabel>
              {draftError && <FormHelperText error>{draftError}</FormHelperText>}
            </Group>
          </Dialog.Content>
          <Dialog.Actions>
            <Button secondary onClick={closeAddModal} disabled={isUpserting}>
              Cancel
            </Button>
            <Button
              primary
              onClick={handleAdd}
              disabled={
                !draft.name || !draft.description.trim() || draft.targetPublications.length === 0 || isUpserting
              }
            >
              {isUpserting ? 'Writing…' : 'Add'}
            </Button>
          </Dialog.Actions>
        </Dialog>
      )}

      {pendingDelete && (
        <ConfirmationDialog
          title="Remove feature flag"
          description={`Are you sure you want to remove the "${pendingDelete.name}" feature flag? You still need to click Save to persist the change.`}
          isLoading={false}
          onClickFunc={handleDeleteConfirm}
          showDialog={(show) => {
            if (!show) setPendingDelete(null);
          }}
          confirmLabel="Remove"
        />
      )}
    </>
  );
}

export default SiteConfigFeatureFlags;
